import {
  Box,
  Button,
  Grid,
  Modal,
  Stack,
  TextField,
  Backdrop,
  Typography,
} from "@mui/material";
import { AllInputPosType } from "../../../../../domain/models/user-return-acknowledgement/user-return-pos";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import moment from "moment";
import { useState } from "react";
import { UserReturnAcknowledgementStore } from "../../../../../store/main/UserReturnAcknowledgementStore";

type FormValues = {
  pos_id: number;
  date: string;
  quantity: number;
};

interface Props<T> {
  open: boolean;
  onClose: () => void;
  data: T;
  onSuccess?: () => void;
}

const AddReturnAcknowledgementModal = <
  T extends AllInputPosType | AllInputPosType
>({
  open,
  onClose,
  data,
  onSuccess,
}: Props<T>) => {
  const { addUserManagerReturnPos, AddUserManagerReturnPosState } =
    UserReturnAcknowledgementStore();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<FormValues>({ mode: "onChange" });

  const onSubmit = async (formData: FormValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const payload = {
      pos_id: data.id,
      date: formData.date,
      quantity: Number(formData.quantity),
    };

    try {
      const response = await addUserManagerReturnPos(payload);

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.message || "Input Acknowledged Successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        onClose();
        onSuccess?.();
      } else {
        Swal.fire({
          icon: "error",
          title: "Acknowledgement Failed",
          text: response.errors?.message || "Something went wrong",
        });
      }
    } catch (err) {
      console.error("Unexpected error while submitting", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backgroundColor: "transparent" },
          },
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "rgba(255, 255, 255, 0.95)",
            boxShadow: 2,
            p: 2,
            width: 400,
            maxWidth: "80vw",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={1}>
            Return Acknowledgement
          </Typography>

          <Grid container>
            <TextField
              label="Return Quantity"
              placeholder="Enter quantity"
              type="text"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              {...register("quantity", {
                required: "Quantity is required",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Only numbers (0-9) are allowed",
                },
                validate: (value) => {
                  const num = Number(value);

                  if (num <= 0) {
                    return "Quantity must be greater than 0";
                  }

                  const returnMax = data.returnable_quantity ?? 0;
                  if (num > returnMax) {
                    return `You entered ${num}, but max returnable is ${returnMax}`;
                  }

                  return true;
                },
              })}
              error={!!errors.quantity}
              helperText={
                errors.quantity?.message ||
                `You can return up to ${data.returnable_quantity ?? 0}`
              }
            />
          </Grid>
          <TextField
            label="Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            {...register("date", {
              required: "Date is required",
              validate: (value) => {
                const selectedDate = moment(value);
                const start = moment().subtract(1, "months").startOf("month");
                const today = moment().endOf("day");

                if (!selectedDate.isBetween(start, today, "day", "[]")) {
                  return `Date must be between ${start.format(
                    "MMM D, YYYY"
                  )} and ${today.format("MMM D, YYYY")}`;
                }

                return true;
              },
            })}
            error={!!errors.date}
            helperText={errors.date?.message}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={1}>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default AddReturnAcknowledgementModal;
